import { store } from '../store';
import { refreshAuthentication, clearAuth } from '../store/slices/authSlice';

// Configuration
const REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes before expiry
const SESSION_CHECK_INTERVAL = 10 * 60 * 1000; // Check session every 10 minutes
const MAX_REFRESH_ATTEMPTS = 3;

class AuthMiddleware {
  private refreshAttempts = 0;
  private lastRefreshTime = 0;
  private sessionCheckInterval: NodeJS.Timeout | null = null;
  private isRefreshing = false;
  private refreshInProgress = false; // Track if refresh is in progress globally
  private lastAuthState = {
    isAuthenticated: false,
    type: null as 'JWT' | 'SESSION' | null
  };

  constructor() {
    this.initialize();
  }

  private initialize() {
    console.log('AuthMiddleware: Initializing...');
    
    // Enable session checking for admin pages with conservative settings
    const isAdminPage = window.location.pathname.startsWith('/admin');
    if (isAdminPage) {
      console.log('AuthMiddleware: Using conservative session checking for admin pages');
      // Add a delay before starting the first session check to allow session cookies to propagate
      setTimeout(() => {
        this.startSessionChecking(300000); // 5 minutes instead of default for admin pages
      }, 5000); // 5 second delay for admin pages
      return;
    }
    
    // Start session checking for session-based auth
    this.startSessionChecking();
    
    // Listen for auth state changes - but only react to meaningful changes
    store.subscribe(() => {
      const state = store.getState().auth;
      const currentAuthState = {
        isAuthenticated: state.isAuthenticated,
        type: state.type
      };
      
      // Only react if authentication status or type actually changed
      if (this.lastAuthState.isAuthenticated !== currentAuthState.isAuthenticated ||
          this.lastAuthState.type !== currentAuthState.type) {
        
        console.log(`AuthMiddleware: Auth state changed - authenticated: ${currentAuthState.isAuthenticated}, type: ${currentAuthState.type}`);
        
        if (currentAuthState.type === 'SESSION' && currentAuthState.isAuthenticated) {
          console.log('AuthMiddleware: Session auth detected, starting session checking');
          this.startSessionChecking();
        } else if (currentAuthState.type === 'JWT' && currentAuthState.isAuthenticated) {
          console.log('AuthMiddleware: JWT auth detected, session checking disabled');
          this.stopSessionChecking();
        } else {
          console.log('AuthMiddleware: No auth or auth cleared, stopping session checking');
          this.stopSessionChecking();
        }
        
        // Update the last known state
        this.lastAuthState = currentAuthState;
      }
    });
  }

  private startSessionChecking(interval: number = SESSION_CHECK_INTERVAL) {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
    }

    // For session authentication, validate session periodically
    this.sessionCheckInterval = setInterval(() => {
      const state = store.getState().auth;
      if (state.type === 'SESSION' && state.isAuthenticated && !this.refreshInProgress) {
        this.validateSession();
      }
    }, interval);
  }

  private stopSessionChecking() {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }
  }

  // For session authentication, validate session with backend
  private async validateSession() {
    if (this.refreshInProgress) {
      console.log('AuthMiddleware: Session validation already in progress, skipping');
      return;
    }

    // Check if there are any active operations that should prevent session validation
    const progressElements = document.querySelectorAll('[data-progress], .progress-indicator, [data-loading="true"]');
    if (progressElements.length > 0) {
      console.log('AuthMiddleware: Skipping session validation - active operations detected');
      return;
    }

    console.log('AuthMiddleware: Starting session validation...');
    this.refreshInProgress = true;

    try {
      // Import the refresh function
      const { refreshAuthentication } = await import('../store/slices/authSlice');
      
      // Dispatch unified refresh action
      const refreshResult = await store.dispatch(refreshAuthentication());
      console.log('AuthMiddleware: Session validation result:', refreshResult);
      
      if (refreshResult.meta.requestStatus === 'rejected') {
        console.error('AuthMiddleware: Session validation failed, clearing auth and dispatching auth-expired event');
        store.dispatch(clearAuth());
        // Use SPA navigation event instead of hard reload
        window.dispatchEvent(new CustomEvent('auth-expired'));
      } else {
        console.log('AuthMiddleware: Session validation successful');
      }
    } catch (error) {
      console.error('AuthMiddleware: Session validation error:', error);
      store.dispatch(clearAuth());
      // Use SPA navigation event instead of hard reload
      window.dispatchEvent(new CustomEvent('auth-expired'));
    } finally {
      this.refreshInProgress = false;
    }
  }

  public async checkTokenExpiry() {
    const state = store.getState().auth;
    
    if (state.type !== 'JWT' || !state.isAuthenticated || !state.tokenExpiry) {
      console.log('AuthMiddleware: Token expiry check skipped - not JWT auth or no expiry');
      return;
    }

    // Check if there are any active operations that should prevent token expiry checks
    const progressElements = document.querySelectorAll('[data-progress], .progress-indicator, [data-loading="true"]');
    if (progressElements.length > 0) {
      console.log('AuthMiddleware: Skipping token expiry check - active operations detected');
      return;
    }

    const now = Date.now();
    const expiryTime = new Date(state.tokenExpiry).getTime();
    const timeUntilExpiry = expiryTime - now;

    console.log(`AuthMiddleware: Token expires in ${Math.round(timeUntilExpiry / 60000)} minutes`);

    if (timeUntilExpiry <= REFRESH_THRESHOLD && !this.refreshInProgress) {
      console.log('AuthMiddleware: Token close to expiry, initiating refresh');
      await this.refreshToken();
    }
  }

  private async refreshToken() {
    if (this.refreshInProgress || this.refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
      console.log(`AuthMiddleware: Refresh skipped - in progress: ${this.refreshInProgress}, attempts: ${this.refreshAttempts}/${MAX_REFRESH_ATTEMPTS}`);
      return;
    }

    console.log(`AuthMiddleware: Starting token refresh (attempt ${this.refreshAttempts + 1}/${MAX_REFRESH_ATTEMPTS})`);
    this.refreshInProgress = true;
    this.refreshAttempts++;

    try {
      const result = await store.dispatch(refreshAuthentication()).unwrap();
      console.log('AuthMiddleware: Token refresh successful');
      this.refreshAttempts = 0; // Reset on success
    } catch (error) {
      console.error('AuthMiddleware: Token refresh failed:', error);
      
      if (this.refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
        console.log('AuthMiddleware: Max refresh attempts reached, logging out user');
        store.dispatch(clearAuth());
        // Use SPA navigation event instead of hard reload
        window.dispatchEvent(new CustomEvent('auth-expired'));
      }
    } finally {
      this.refreshInProgress = false;
    }
  }
}

// Export singleton instance
export const authMiddleware = new AuthMiddleware(); 